/**
 * @fileoverview Página responsável por listar e administrar mensagens rápidas.
 */

import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Popover from "@material-ui/core/Popover";
import Grow from "@material-ui/core/Grow";
import { Pencil, Plus, Trash2 as Trash, MoreVertical, Search, Paperclip } from "lucide-react";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";


const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    //console.log("aqui");
    //console.log(action);
    //console.log(action.payload);
    const quickmessages = action.payload;
    const newQuickmessages = [];
    //console.log(newQuickmessages);

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;

    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: theme.spacing(1.5),
      gap: theme.spacing(1.25),
    },
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  headerTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  mobileMenuButton: {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(15, 23, 42, 0.02)",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.85)" : "#475569",
    cursor: "pointer",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    padding: 0,
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(15, 23, 42, 0.04)",
      borderColor: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.35)"
        : "rgba(59, 130, 246, 0.3)",
    },
  },
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  cmdMenuPaper: {
    borderRadius: 14,
    overflow: "visible",
    background: theme.palette.type === "dark" ? "#1c1f22" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: theme.palette.type === "dark"
      ? "0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 6px 14px -4px rgba(0, 0, 0, 0.3)"
      : "0 20px 40px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -4px rgba(15, 23, 42, 0.10)",
  },
  cmdMenu: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 4px",
    minWidth: 232,
    fontFamily: "'Montserrat', sans-serif",
  },
  cmdMenuSection: {
    display: "flex",
    flexDirection: "column",
  },
  cmdMenuEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "8px 12px 4px",
    color: theme.palette.type === "dark" ? "#6b7380" : "#94A3B8",
  },
  cmdMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    width: "100%",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e6e8eb" : "#1a1a1a",
    transition: "background-color 140ms ease",
    animation: "$cmdMenuItemIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(15, 23, 42, 0.04)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
      outlineOffset: 1,
    },
    "&:nth-child(1)": { animationDelay: "0ms" },
    "&:nth-child(2)": { animationDelay: "35ms" },
    "&:nth-child(3)": { animationDelay: "70ms" },
  },
  cmdMenuIconBox: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
  },
  cmdMenuIconBoxCobalt: {
    background: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.16)" : "#EEF2FF",
    color: theme.palette.type === "dark" ? "#93a6ff" : "#3D5CFD",
  },
  cmdMenuLabel: {
    flex: 1,
  },
  "@keyframes cmdMenuItemIn": {
    "0%": { opacity: 0, transform: "translateY(4px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  addButton: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
    height: 36,
    padding: '0 14px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    gap: 8,
    border: 'none',
    boxShadow: 'none',
    background: 'linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)',
    color: '#ffffff',
    transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: 'linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)',
      boxShadow: 'none',
    },
    '& svg': { flexShrink: 0 },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(2),
    overflowY: "scroll",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.5)' : '1px solid #3D5CFD',
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 4px rgba(91, 124, 255, 0.15), 0 8px 24px rgba(91, 124, 255, 0.2)'
          : '0 0 0 4px rgba(61, 92, 253, 0.1), 0 8px 24px rgba(61, 92, 253, 0.15)',
      },
      '& fieldset': {
        border: 'none',
      },
    },
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  card: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.6) 0%, rgba(22, 22, 24, 0.6) 100%)'
      : '#FFFFFF',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: "20px",
    boxShadow: theme.palette.type === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: 'visible',
    position: 'relative',
    "&:hover": {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 12px 40px rgba(91, 124, 255, 0.25)'
        : '0 8px 30px rgba(61, 92, 253, 0.15)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(61, 92, 253, 0.2)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  shortcode: {
    fontSize: '1.25rem',
    fontWeight: 700,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)'
      : 'linear-gradient(135deg, #3D5CFD 0%, #7C3AED 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(0.5),
  },
  messagePreview: {
    fontSize: '0.9rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: '12px',
    background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
    maxHeight: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
  },
  attachmentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: '4px 10px',
    borderRadius: '8px',
    background: theme.palette.type === 'dark'
      ? 'rgba(139, 92, 246, 0.15)'
      : 'rgba(139, 92, 246, 0.1)',
    color: theme.palette.type === 'dark' ? '#c4b5fd' : '#7c3aed',
    border: theme.palette.type === 'dark' ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.2)',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: theme.spacing(1.5),
  },
  cardActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  iconButton: {
    padding: theme.spacing(1),
    borderRadius: '10px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  editButton: {
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(59, 130, 246, 0.08)',
    },
  },
  deleteButton: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.08)',
    },
  },
}));

const Quickemessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileHeaderMenuAnchor, setMobileHeaderMenuAnchor] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company${companyId}-quickemessage`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const companyId = user.companyId;
      //const searchParam = ({ companyId, userId: user.id });
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber, userId: user.id },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    //window.location.reload();
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    //console.log(quickemessage);
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });

  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer className={classes.root}>
      <ConfirmationModal
        title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <div className={classes.header}>
        {isMobile ? (
          <>
            <div className={classes.headerTitleRow}>
              <Typography className={classes.title}>
                {i18n.t("quickMessages.title")}
              </Typography>
              <button
                type="button"
                className={classes.mobileMenuButton}
                onClick={(e) => setMobileHeaderMenuAnchor(e.currentTarget)}
                aria-label="Mais opções"
                aria-haspopup="menu"
                aria-expanded={Boolean(mobileHeaderMenuAnchor)}
              >
                <MoreVertical size={20} strokeWidth={1.75} />
              </button>
            </div>
            <TextField
              placeholder={i18n.t("quickMessages.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} strokeWidth={1.75} color={theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'} />
                  </InputAdornment>
                ),
              }}
            />
            <Popover
              open={Boolean(mobileHeaderMenuAnchor)}
              anchorEl={mobileHeaderMenuAnchor}
              onClose={() => setMobileHeaderMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              TransitionComponent={Grow}
              transitionDuration={{ enter: 200, exit: 140 }}
              PaperProps={{ className: classes.cmdMenuPaper }}
            >
              <div className={classes.cmdMenu} role="menu">
                <div className={classes.cmdMenuSection}>
                  <div className={classes.cmdMenuEyebrow}>Adicionar</div>
                  <button
                    type="button"
                    role="menuitem"
                    className={classes.cmdMenuItem}
                    onClick={() => {
                      setMobileHeaderMenuAnchor(null);
                      handleOpenQuickMessageDialog();
                    }}
                  >
                    <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxCobalt}`}>
                      <Plus size={16} strokeWidth={1.8} />
                    </span>
                    <span className={classes.cmdMenuLabel}>
                      {i18n.t("quickMessages.buttons.add")}
                    </span>
                  </button>
                </div>
              </div>
            </Popover>
          </>
        ) : (
          <>
            <Typography className={classes.title}>
              {i18n.t("quickMessages.title")}
            </Typography>
            <div className={classes.buttonsWrapper}>
              <TextField
                placeholder={i18n.t("quickMessages.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                className={classes.searchField}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} strokeWidth={1.75} color={theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenQuickMessageDialog}
                data-onboarding-tour="quickMessage"
                className={classes.addButton}
                startIcon={<Plus size={15} strokeWidth={1.75} />}
              >
                {i18n.t("quickMessages.buttons.add")}
              </Button>
            </div>
          </>
        )}
      </div>
      <Paper
        className={classes.mainPaper}
        onScroll={handleScroll}
      >
        <div className={classes.cardsContainer}>
          {quickemessages.sort((a, b) => a.id - b.id).map((quickemessage) => (
            <Card key={quickemessage.id} className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <div style={{ flex: 1 }}>
                    <Typography className={classes.shortcode}>
                      /{quickemessage.shortcode}
                    </Typography>
                  </div>
                  <div className={classes.cardActions}>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.editButton}`}
                      onClick={() => handleEditQuickemessage(quickemessage)}
                      title="Editar"
                    >
                      <Pencil size={18} strokeWidth={1.5} color="#5490ff" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.deleteButton}`}
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingQuickemessage(quickemessage);
                      }}
                      title="Deletar"
                    >
                      <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                    </IconButton>
                  </div>
                </div>

                {quickemessage.message && (
                  <Typography className={classes.messagePreview}>
                    {quickemessage.message}
                  </Typography>
                )}

                {quickemessage.mediaName && (
                  <Box display="flex" alignItems="center">
                    <span className={classes.attachmentBadge}>
                      <Paperclip size={14} strokeWidth={1.75} />
                      {quickemessage.mediaName}
                    </span>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Quickemessages;
